/**
 * Zoom and pan maths for `PhotoLightbox`, kept pure so it can be tested.
 *
 * The image is drawn with `translate(x, y) scale(scale)` around its centre, and
 * the image sits centred in its container. Points are in container pixels,
 * measured from the container centre.
 *
 * Every gesture computes its result from the view at the START of the gesture,
 * never from the previous frame: a pinch that mixed the two drifted a little on
 * every move event and threw the image off screen on a phone.
 */

export interface View {
  scale: number;
  x: number;
  y: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export const IDENTITY_VIEW: View = { scale: 1, x: 0, y: 0 };
export const MIN_SCALE = 0.5;
export const MAX_SCALE = 5;
export const DOUBLE_TAP_SCALE = 2.5;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Zoom to `scale`, keeping the image point under `at` where it is. */
export function zoomAt(start: View, at: Point, scale: number): View {
  const newScale = clamp(scale, MIN_SCALE, MAX_SCALE);
  const imgX = (at.x - start.x) / start.scale;
  const imgY = (at.y - start.y) / start.scale;
  return { scale: newScale, x: at.x - imgX * newScale, y: at.y - imgY * newScale };
}

/**
 * Two-finger pinch: the image point that was between the fingers at the start
 * stays between them, so the fingers both zoom and move the image.
 */
export function pinchView(start: View, startMid: Point, startDist: number, mid: Point, dist: number): View {
  if (startDist <= 0) return start;
  const scale = clamp(start.scale * (dist / startDist), MIN_SCALE, MAX_SCALE);
  const imgX = (startMid.x - start.x) / start.scale;
  const imgY = (startMid.y - start.y) / start.scale;
  return { scale, x: mid.x - imgX * scale, y: mid.y - imgY * scale };
}

/**
 * Keep the image on screen: an axis the scaled image does not fill is centred,
 * an axis it overflows can pan only until its edge reaches the container edge.
 * `image` is the unscaled size the image is laid out at. An unknown size (not
 * loaded yet) leaves the view as it is.
 */
export function clampView(view: View, image: Size, container: Size): View {
  if (image.width <= 0 || image.height <= 0 || container.width <= 0 || container.height <= 0) return view;
  const slackX = Math.max(0, (image.width * view.scale - container.width) / 2);
  const slackY = Math.max(0, (image.height * view.scale - container.height) / 2);
  return {
    scale: view.scale,
    x: slackX > 0 ? clamp(view.x, -slackX, slackX) : 0,
    y: slackY > 0 ? clamp(view.y, -slackY, slackY) : 0,
  };
}
